/**
 * workflows/importWorkflow.ts
 *
 * New COPY_IN / MOVE_IN workflow using the extracted architecture:
 *   Source (selected nodes) → BL Context → MetadataEditor → Copy/Move + Rename + BL
 *
 * Replaces the legacy flow:
 *   creativeImportRequested → importCreativeFiles → loadBLAndFillJobs
 *     → checkSubmit → importCreative (copyMoveJob + renameJob + blCreate)
 */

import Notifications from 'react-notification-system-redux';
import { api } from '../../../../general/apiScenarios';
import { CHNode, CHNodeIsFolder } from '../../../../general/node';
import { partition } from 'lodash';
import { ListenerApiLight } from '../../../listenerMiddleware';
import { loadBLContext } from '../blContextService';
import {
  _closeEditor,
  _setSubmitInProgress,
  requestUserValidation,
} from '../metadataEditor/MetadataEditorService';
import { importAndAttach, type ImportMode } from '../executionAdapters/importAdapter';
import { JobMode } from '../legacy_models/model';
import type { PreparableFile, FileExecutionResult, CreativeBLSettings } from '../contracts';
import * as fileManagerActions from '../../fileManager/actions';

async function loadOccupiedFilenames(folderNodeId: number) {
  const result = await api.fs.loadNodes(folderNodeId);
  if (!result.success) {
    return [] as string[];
  }

  return result.data.response.nodes
    .filter((node) => node.type !== 'folder')
    .map((node) => node.name);
}

// ─── Workflow ────────────────────────────────────────────────────────────────

/**
 * Import files from one location to a creative folder via copy or move.
 *
 * @param lapi  Listener API for dispatch/signal
 * @param sourceNodes  Nodes selected by the user
 * @param targetNodeId  Creative folder node ID
 * @param mode  'COPY' or 'MOVE'
 */
export async function importWorkflow(
  lapi: ListenerApiLight,
  sourceNodes: CHNode[],
  targetNodeId: number,
  mode: ImportMode,
) {
  // 1. Filter out folders (warn user)
  const [folders, files] = partition(sourceNodes, CHNodeIsFolder);
  if (folders.length) {
    lapi.dispatch(Notifications.warning({ title: 'Can not import folders' }));
  }
  if (files.length === 0) return;

  // 2. Load BL context
  const ctxResult = await loadBLContext(lapi, targetNodeId);
  lapi.signal.throwIfAborted();

  if (!ctxResult.success) {
    lapi.dispatch(
      Notifications.error({ title: 'Error', message: 'Failed to load creative context' }),
    );
    return;
  }

  const context: CreativeBLSettings = ctxResult.data;
  const jobMode = mode === 'COPY' ? JobMode.COPY_IN : JobMode.MOVE_IN;

  // 3. Convert to PreparableFile format
  const preparableFiles: PreparableFile[] = files.map((node) => ({
    id: String(node.id),
    originalName: node.name,
    relpath: '',
    nodeRef: node,
  }));
  const occupiedFilenames = await loadOccupiedFilenames(targetNodeId);
  lapi.signal.throwIfAborted();

  // 4. Open MetadataEditor
  const preparedFiles = await requestUserValidation(preparableFiles, context, {
    mode: jobMode,
    occupiedFilenames,
  });

  if (!preparedFiles) return; // cancelled

  try {
    lapi.signal.throwIfAborted();

    // 5. Execute import for each file
    const results: FileExecutionResult[] = [];
    for (const file of preparedFiles) {
      lapi.signal.throwIfAborted();
      const result = await importAndAttach(file, targetNodeId, context, mode, lapi.signal);
      results.push(result);
    }

    // 6. Report
    const failed = results.filter((r) => !r.success);
    if (failed.length === 0) {
      lapi.dispatch(
        Notifications.success({ title: 'All creatives have been imported successfully' }),
      );
    } else {
      const failedNames = failed
        .map((f) => {
          if (!f.success) {
            const file = preparedFiles.find((pf) => pf.id === f.fileId);
            return file?.originalName ?? f.fileId;
          }
          return '';
        })
        .filter(Boolean);
      lapi.dispatch(
        Notifications.error({
          title: 'Import error',
          message: `Failed to import: ${failedNames.join(', ')}`,
        }),
      );
    }

    // 7. Refresh file manager
    lapi.dispatch(fileManagerActions.refreshRequested());
  } finally {
    _setSubmitInProgress(false);
    _closeEditor();
  }
}
